import React, { useState, useEffect, useContext, useRef } from "react";
import "../adminModals.css";
import "./imageModal.css";
import { CombinedContext } from "../../../context/combinedContext";
import companyBasedRequest from "../../../request/companyBasedRequest";
import translations from "../../../translations/translations";
import { ChevronLeft, ChevronRight, Edit, GripHorizontal, Plus, Star, Trash2, TriangleAlert } from "lucide-react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import ImageCropper from "../../common/imageCropper/imageCropper";
import BannerMessageFixed from "../../common/bannerMessageFixed/bannerMessageFixed";
import useModalDismiss from "../../common/useModalDismiss";

const MAX_RAW_FILE_BYTES = 20 * 1024 * 1024; // 20MB guard before we even try to load/crop it

/* Reordering needs an id per photo that survives the move, and the same
   URL can legitimately sit in the list twice — so number the repeats. */
const thumbIds = (images) => {
	const seen = new Map();
	return images.map((url) => {
		const n = seen.get(url) || 0;
		seen.set(url, n + 1);
		return `${url}#${n}`;
	});
};

/** Simple mobile swipe carousel (controlled) */
function SwipeCarousel({ images = [], index, onChange }) {
	const { language } = useContext(CombinedContext);

	const trackRef = useRef(null);
	const [dragging, setDragging] = useState(false);
	const startX = useRef(0);
	const deltaX = useRef(0);

	const total = images.length || 1;

	const goTo = (n) => onChange(((n % total) + total) % total);
	const next = () => goTo(index + 1);
	const prev = () => goTo(index - 1);

	useEffect(() => {
		const onKey = (e) => {
			// Arrows also drive a keyboard reorder down in the thumb strip —
			// whoever claimed the key first keeps it.
			if (e.defaultPrevented) return;
			if (e.key === "ArrowRight") next();
			if (e.key === "ArrowLeft") prev();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [index, total]);

	const onStart = (clientX) => {
		setDragging(true);
		startX.current = clientX;
		deltaX.current = 0;
	};
	const onMove = (clientX) => {
		if (!dragging) return;
		deltaX.current = clientX - startX.current;
		if (trackRef.current) {
			trackRef.current.style.transition = "none";
			trackRef.current.style.transform = `translateX(calc(${-index * 100}% + ${deltaX.current}px))`;
		}
	};
	const onEnd = () => {
		setDragging(false);
		if (Math.abs(deltaX.current) > 60) {
			if (deltaX.current < 0) next();
			else prev();
		}
		if (trackRef.current) {
			trackRef.current.style.transition = "";
			trackRef.current.style.transform = `translateX(${-index * 100}%)`;
		}
		deltaX.current = 0;
	};

	useEffect(() => {
		if (trackRef.current) {
			trackRef.current.style.transition = "transform 300ms ease";
			trackRef.current.style.transform = `translateX(${-index * 100}%)`;
		}
	}, [index]);

	if (images.length === 0) {
		return (
			<div className="sf-pm-img-stage is-empty">
				<span className="sf-pm-img-placeholder">
					{translations[language].profile?.images?.noImage || "No image"}
				</span>
			</div>
		);
	}

	return (
		<div
			className="sf-pm-img-stage"
			onTouchStart={(e) => onStart(e.touches[0].clientX)}
			onTouchMove={(e) => onMove(e.touches[0].clientX)}
			onTouchEnd={onEnd}
			onMouseDown={(e) => {
				e.preventDefault();
				onStart(e.clientX);
				const mm = (evt) => onMove(evt.clientX);
				const mu = () => {
					onEnd();
					window.removeEventListener("mousemove", mm);
					window.removeEventListener("mouseup", mu);
				};
				window.addEventListener("mousemove", mm);
				window.addEventListener("mouseup", mu, { once: true });
			}}
		>
			<div className="sf-pm-img-track" ref={trackRef}>
				{images.map((img, i) => (
					<div className="sf-pm-img-slide" key={i} aria-hidden={i !== index}>
						<img src={img} alt={`Slide ${i + 1}`} draggable={false} />
					</div>
				))}
			</div>

			{images.length > 1 && (
				<>
					<button
						type="button"
						className="sf-pm-img-arrow is-left"
						onClick={prev}
						aria-label="Previous image"
					>
						<ChevronLeft size={20} strokeWidth={2.2} />
					</button>
					<button
						type="button"
						className="sf-pm-img-arrow is-right"
						onClick={next}
						aria-label="Next image"
					>
						<ChevronRight size={20} strokeWidth={2.2} />
					</button>
				</>
			)}
		</div>
	);
}

const ImageModal = ({ isOpen, onClose, shopId, initialImages = [], onSave }) => {
	const { language, setUserData, userData } = useContext(CombinedContext);

	// data
	const [images, setImages] = useState([]);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [uploading, setUploading] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);

	/* the stacked delete confirm owns Escape while it is open */
	useModalDismiss(onClose, { enabled: isOpen && !showConfirm });
	useModalDismiss(() => cancelDelete(), { enabled: isOpen && showConfirm });
	const [isReordering, setIsReordering] = useState(false);
	const [saving, setSaving] = useState(false);

	// crop + banner state
	const [cropSrc, setCropSrc] = useState(null);
	const [cropMode, setCropMode] = useState(null); // "add" | "replace"
	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);

	const imgT = translations[language].profile?.images || {};

	const showBanner = (type, text, duration = 4000) => {
		setBanner({ type, text });
		setExiting(false);
		setTimeout(() => setExiting(true), duration - 250);
		setTimeout(() => { setBanner(null); setExiting(false); }, duration);
	};

	// file inputs
	const addInputRef = useRef(null);
	const replaceInputRef = useRef(null);
	const deleteIndexRef = useRef(null);

	useEffect(() => {
		setImages(initialImages || []);
		setSelectedIndex(0);
	}, [initialImages]);

	if (!isOpen) return null;

	/* Reordering used to ride on the HTML5 draggable attribute, which never
	   fires on a touch screen — on a phone "Set as cover" was the only way
	   to move a photo at all. Same pointer/touch/keyboard drag the services
	   list uses. */
	const handleReorderEnd = ({ source, destination }) => {
		setIsReordering(false);
		if (!destination || destination.index === source.index) return;

		const from = source.index;
		const to = destination.index;

		setImages((prev) => {
			const next = [...prev];
			const [moved] = next.splice(from, 1);
			next.splice(to, 0, moved);
			return next;
		});

		// Follow the photo that moved, not whatever slid into its slot.
		setSelectedIndex((cur) => {
			if (cur === from) return to;
			if (from < cur && to >= cur) return cur - 1;
			if (from > cur && to <= cur) return cur + 1;
			return cur;
		});
	};

	const handleSave = async () => {
		// An empty gallery has nothing to persist, and saving one used to tick the
		// onboarding step with no photo behind it.
		if (images.length === 0) {
			showBanner("error", imgT.needPhoto || "Add at least one photo before saving.");
			return;
		}

		try {
			setSaving(true);
			if (onSave) onSave(images);

			const res = await companyBasedRequest.updateShopImages({
				shopId,
				images,
				accessToken: userData.cognitoTokens.AccessToken,
			});

			setUserData((prev) => ({ ...prev, shop: res.shop }));
			onClose();
		} catch (err) {
			console.error("Failed to update shop images:", err);
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteRequest = (index) => {
		deleteIndexRef.current = index;
		setShowConfirm(true);
	};

	const confirmDelete = async () => {
		const idx = deleteIndexRef.current;
		const next = images.filter((_, i) => i !== idx).filter(Boolean);

		if (next.length === 0) setSelectedIndex(0);
		else if (idx >= next.length) setSelectedIndex(next.length - 1);
		setImages(next);

		setShowConfirm(false);
		deleteIndexRef.current = null;

		try {
			const res = await companyBasedRequest.updateShopImages({
				shopId,
				images: next,
				accessToken: userData.cognitoTokens.AccessToken,
			});
			setUserData((prev) => ({ ...prev, shop: res.shop }));
			onSave?.(next);
		} catch (err) {
			console.error("Failed to delete image:", err);
		}
	};

	const cancelDelete = () => {
		setShowConfirm(false);
		deleteIndexRef.current = null;
	};

	// ---- helpers

	const uploadBlobAndGetUrl = async (blob, fileName) => {
		const base64 = await new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result.split(",")[1]);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});

		const res = await companyBasedRequest.uploadShopImage({
			file: base64,
			fileName,
			fileType: blob.type || "image/jpeg",
			shopId,
			accessToken: userData.cognitoTokens.AccessToken,
		});
		return res.fileUrl;
	};

	const pickFile = (e, mode) => {
		const file = e.target.files?.[0];
		e.target.value = "";
		if (!file || !shopId) return;

		if (!file.type.startsWith("image/")) {
			showBanner("error", imgT.invalidImage || "That file isn't a valid image.");
			return;
		}
		if (file.size > MAX_RAW_FILE_BYTES) {
			showBanner("error", imgT.tooLarge || "That image is too large. Please choose a smaller photo.");
			return;
		}

		setCropMode(mode);
		setCropSrc(URL.createObjectURL(file));
	};

	const handleAddFiles = (e) => pickFile(e, "add");
	const handleReplaceFile = (e) => pickFile(e, "replace");

	const closeCropper = () => {
		if (cropSrc) URL.revokeObjectURL(cropSrc);
		setCropSrc(null);
		setCropMode(null);
	};

	const handleCropSave = async (blob) => {
		setUploading(true);
		try {
			const url = await uploadBlobAndGetUrl(blob, `shop-photo-${Date.now()}.jpg`);
			if (cropMode === "replace") {
				setImages((prev) => {
					const next = [...prev];
					next[selectedIndex] = url;
					return next;
				});
			} else {
				setImages((prev) => {
					const next = [...prev, url];
					if (prev.length === 0) setSelectedIndex(0);
					return next;
				});
			}
		} catch (err) {
			console.error("Failed to upload image:", err);
			showBanner("error", imgT.uploadFailed || "We couldn't upload the image. Please try again.");
		} finally {
			setUploading(false);
			closeCropper();
		}
	};

	const setAsCover = () => {
		if (selectedIndex === 0) return;
		setImages((prev) => {
			const next = [...prev];
			const [img] = next.splice(selectedIndex, 1);
			next.unshift(img);
			return next;
		});
		setSelectedIndex(0);
	};

	const t = translations[language].profile;
	const hasImages = images.length > 0;

	// ---- RENDER
	return (
		<>
		<div className="sf-pm-overlay">
			<div className="sf-pm-modal is-wide">
				{/* HEADER */}
				<div className="sf-pm-header">
					<div className="sf-pm-heading">
						<h2 className="sf-pm-title">{imgT.title || "Images"}</h2>
						<p className="sf-pm-sub">{t.modal.images.description}</p>
					</div>
					{/* Dismissing discards, like the services modal next door: only
					    Save writes the gallery. This used to hand the unsaved order to
					    the profile card, which read as a save that never reached the
					    server and came back in the old order on the next load. */}
					<button className="sf-pm-close" onClick={onClose} aria-label={t.actions.cancel}>
						✕
					</button>
				</div>

				{/* BODY */}
				<div className="sf-pm-body">
					<section className="sf-pm-section">
						{/* STAGE — the cover is whatever sits first, so the badge
							travels with position rather than with a flag. */}
						<div className="sf-pm-img-frame">
							<SwipeCarousel images={images} index={selectedIndex} onChange={setSelectedIndex} />

							{hasImages && (
								<>
									{selectedIndex === 0 && (
										<span className="sf-pm-img-cover-badge">
											<Star size={11} strokeWidth={2.6} fill="currentColor" />
											{imgT.cover || "Cover"}
										</span>
									)}

									{images.length > 1 && (
										<span className="sf-pm-img-counter">
											{selectedIndex + 1} / {images.length}
										</span>
									)}
								</>
							)}
						</div>

						{/* Actions live under the photo, not over it — three labels
							don't fit across the frame in either language, and light
							text on someone else's photo is a contrast gamble. */}
						{hasImages && (
							<div className="sf-pm-img-actions">
								<button
									type="button"
									className="sf-pm-img-act"
									onClick={setAsCover}
									disabled={selectedIndex === 0}
								>
									<Star size={15} strokeWidth={2} />
									<span>{imgT.makeCover || "Set as cover"}</span>
								</button>

								<button
									type="button"
									className="sf-pm-img-act"
									onClick={() => replaceInputRef.current?.click()}
								>
									<Edit size={15} strokeWidth={2} />
									<span>{imgT.replace || "Replace"}</span>
								</button>

								<button
									type="button"
									className="sf-pm-img-act is-danger"
									onClick={() => handleDeleteRequest(selectedIndex)}
								>
									<Trash2 size={15} strokeWidth={2} />
									<span>{imgT.delete || "Delete"}</span>
								</button>
							</div>
						)}

						<input
							ref={replaceInputRef}
							type="file"
							accept="image/*"
							style={{ display: "none" }}
							onChange={handleReplaceFile}
						/>

						{/* THUMB STRIP — tap a photo to preview it, press and drag it
						    to move it. Pointer, touch and keyboard, unlike the HTML5
						    draggable attribute this replaces. */}
						<DragDropContext
							onDragStart={() => setIsReordering(true)}
							onDragEnd={handleReorderEnd}
						>
							<Droppable droppableId="sf-pm-images" direction="horizontal">
								{(dropProvided) => (
									<div className={`sf-pm-img-thumbs ${isReordering ? "is-reordering" : ""}`}>
										<button
											className="sf-pm-img-thumb is-add"
											onClick={() => addInputRef.current?.click()}
											type="button"
											disabled={uploading}
											aria-label={imgT.addPhoto || "Add photo"}
										>
											<Plus size={20} strokeWidth={2.2} />
										</button>

										<div
											className="sf-pm-img-thumb-row"
											ref={dropProvided.innerRef}
											{...dropProvided.droppableProps}
										>
											{thumbIds(images).map((id, i) => (
												<Draggable key={id} draggableId={id} index={i}>
													{(dragProvided, dragSnapshot) => (
														<div
															className={`sf-pm-img-thumb ${i === selectedIndex ? "is-active" : ""} ${dragSnapshot.isDragging ? "is-dragging" : ""}`}
															ref={dragProvided.innerRef}
															{...dragProvided.draggableProps}
															{...dragProvided.dragHandleProps}
															onClick={() => setSelectedIndex(i)}
															aria-label={`Image ${i + 1}`}
														>
															<img src={images[i]} alt="" draggable={false} />

															{/* The gesture said out loud — at 68px there is no
															    room for a word, and nothing else on the thumb
															    suggests it can be picked up. */}
															<span className="sf-pm-img-thumb-grip" aria-hidden="true">
																<GripHorizontal size={12} strokeWidth={2.6} />
															</span>

															{i === 0 && <span className="sf-pm-img-thumb-cover" aria-hidden="true" />}
														</div>
													)}
												</Draggable>
											))}
											{dropProvided.placeholder}
										</div>

										<input
											ref={addInputRef}
											type="file"
											accept="image/*"
											style={{ display: "none" }}
											onChange={handleAddFiles}
										/>
									</div>
								)}
							</Droppable>
						</DragDropContext>

						{images.length > 1 && (
							<p className="sf-pm-img-hint">
								<GripHorizontal size={13} strokeWidth={2.6} />
								<span>{imgT.reorderHint || "Hold a photo and drag it to change the order."}</span>
							</p>
						)}

						{!hasImages && <p className="sf-pm-empty">{imgT.noPhotos || "No photos yet"}</p>}
					</section>
				</div>

				{/* FOOTER */}
				<div className="sf-pm-footer">
					<button className="sf-pm-btn is-ghost" onClick={onClose} disabled={uploading || saving}>
						{translations[language].calendar.editor.cancel}
					</button>
					<button className="sf-pm-btn is-primary" onClick={handleSave} disabled={saving || uploading || images.length === 0}>
						{saving && <span className="sf-pm-spinner" aria-hidden="true" />}
						{saving
							? translations[language].calendar.editor.saving
							: translations[language].calendar.editor.save}
					</button>
				</div>
			</div>

			{showConfirm && (
				<div className="sf-pm-confirm-overlay">
					<div className="sf-pm-confirm">
						<span className="sf-pm-confirm-icon" aria-hidden="true">
							<TriangleAlert size={22} strokeWidth={2} />
						</span>
						<p>{imgT.confirmDelete || "Are you sure you want to delete this image?"}</p>
						<div className="sf-pm-confirm-actions">
							<button className="sf-pm-btn is-ghost" onClick={cancelDelete}>
								{t.actions?.cancel || "Cancel"}
							</button>
							<button className="sf-pm-btn is-danger" onClick={confirmDelete}>
								{imgT.delete || "Delete"}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>

		{cropSrc && (
			<ImageCropper
				imageSrc={cropSrc}
				aspect={4 / 3}
				cropShape="rect"
				title={imgT.cropTitle || "Crop photo"}
				cancelLabel={translations[language].calendar.editor.cancel}
				saveLabel={translations[language].calendar.editor.save}
				savingLabel={translations[language].calendar.editor.saving}
				zoomLabel={imgT.zoom || "Zoom"}
				onCancel={closeCropper}
				onSave={handleCropSave}
			/>
		)}

		{banner && <BannerMessageFixed type={banner.type} text={banner.text} exiting={exiting} />}
		</>
	);
};

export default ImageModal;
